import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Search, DollarSign, Users, ChevronRight, X } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'
import { track } from '../lib/analytics'

export default function Onboarding() {
const { user, profile } = useAuth()
const navigate = useNavigate()
const [searchParams] = useSearchParams()
// Where they were heading before sign-up (e.g. the listing they were booking)
const nextParam = searchParams.get('next')
const next = nextParam && nextParam.startsWith('/') && !nextParam.startsWith('//') ? nextParam : null
const [step, setStep] = useState(1)
const [intent, setIntent] = useState(null)
const [saving, setSaving] = useState(false)

const firstName = profile?.full_name?.split(' ')[0] || 'there'

const saveIntentAndContinue = async (chosenIntent) => {
track('onboarding_intent', { intent: chosenIntent })
setIntent(chosenIntent)
setSaving(true)
if (user) {
await supabase.from('profiles').update({ user_intent: chosenIntent }).eq('id', user.id)
}
setSaving(false)
// Heading back to a booking/message? Go straight there once intent is saved
if (next) { navigate(next); return }
setStep(3)
}

const handleSkip = () => {
track('onboarding_skip', { step })
navigate(next || '/')
}

const cardStyle = (active) => ({
padding: '18px 20px',
borderRadius: 14,
border: active ? '2px solid var(--primary)' : '2px solid var(--border)',
background: active ? 'var(--primary-pale)' : 'white',
cursor: 'pointer',
transition: 'all 0.15s ease',
display: 'flex',
alignItems: 'center',
gap: 14,
})

const iconWrap = {
width: 48,
height: 48,
minWidth: 48,
borderRadius: 12,
background: 'linear-gradient(135deg, #2D3FCC 0%, #7B3FE4 100%)',
color: 'white',
display: 'flex',
alignItems: 'center',
justifyContent: 'center',
flexShrink: 0,
}

return (
<div style={{
minHeight: '100vh',
background: 'linear-gradient(135deg, #2D3FCC 0%, #7B3FE4 100%)',
display: 'flex',
alignItems: 'center',
justifyContent: 'center',
padding: '1.5rem 1rem',
}}>
<div style={{
width: '100%',
maxWidth: 480,
background: 'white',
borderRadius: 20,
padding: '2rem 1.5rem',
position: 'relative',
boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
}}>
<button onClick={handleSkip} style={{
position: 'absolute',
top: 16,
right: 16,
background: 'transparent',
border: 'none',
color: 'var(--text-muted)',
cursor: 'pointer',
padding: 6,
}} aria-label="Skip onboarding">
<X size={20} />
</button>

{/* Progress dots */}
<div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginBottom: 24 }}>
{[1, 2, 3].map(s => (
<div key={s} style={{
width: s === step ? 24 : 8,
height: 8,
borderRadius: 99,
background: s <= step ? 'var(--primary)' : 'var(--gray-200)',
transition: 'all 0.2s ease',
}} />
))}
</div>

{step === 1 && (
<div style={{ textAlign: 'center' }}>
<div style={{ fontSize: 56, marginBottom: 16 }}>👋</div>
<h1 style={{ fontSize: 26, fontWeight: 700, color: 'var(--navy)', marginBottom: 12, letterSpacing: '-0.02em' }}>
Welcome to HireIt, {firstName}!
</h1>
<p style={{ fontSize: 15, color: 'var(--text-muted)', lineHeight: 1.6, marginBottom: 28 }}>
Hire what you need from people nearby, or earn extra by listing your own items, equipment or services. It can be almost anything.
</p>
<button
className="btn btn-gradient btn-lg btn-full"
onClick={() => setStep(2)}
style={{ marginBottom: 12 }}
>
Get started <ChevronRight size={18} />
</button>
<button
onClick={handleSkip}
style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 13, cursor: 'pointer' }}
>
Skip — I'll figure it out
</button>
</div>
)}

{step === 2 && (
<div>
<h2 style={{ fontSize: 22, fontWeight: 700, color: 'var(--navy)', marginBottom: 8, textAlign: 'center' }}>
What brings you here?
</h2>
<p style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 24, textAlign: 'center' }}>
We'll personalise things for you. Pick one.
</p>

<div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
<button
onClick={() => saveIntentAndContinue('hire')}
style={cardStyle(intent === 'hire')}
disabled={saving}
>
<div style={iconWrap}><Search size={22} /></div>
<div style={{ textAlign: 'left', flex: 1 }}>
<div style={{ fontWeight: 700, color: 'var(--navy)', fontSize: 15 }}>I want to hire something</div>
<div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>Browse items near me</div>
</div>
<ChevronRight size={18} color="var(--text-muted)" />
</button>

<button
onClick={() => saveIntentAndContinue('earn')}
style={cardStyle(intent === 'earn')}
disabled={saving}
>
<div style={iconWrap}><DollarSign size={22} /></div>
<div style={{ textAlign: 'left', flex: 1 }}>
<div style={{ fontWeight: 700, color: 'var(--navy)', fontSize: 15 }}>I want to earn money</div>
<div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>Hire out my items, equipment or services</div>
</div>
<ChevronRight size={18} color="var(--text-muted)" />
</button>

<button
onClick={() => saveIntentAndContinue('both')}
style={cardStyle(intent === 'both')}
disabled={saving}
>
<div style={iconWrap}><Users size={22} /></div>
<div style={{ textAlign: 'left', flex: 1 }}>
<div style={{ fontWeight: 700, color: 'var(--navy)', fontSize: 15 }}>Both — I'm in the community</div>
<div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>Hire and earn</div>
</div>
<ChevronRight size={18} color="var(--text-muted)" />
</button>
</div>

<div style={{ textAlign: 'center' }}>
<button
onClick={handleSkip}
style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 13, cursor: 'pointer' }}
>
Skip this step
</button>
</div>
</div>
)}

{step === 3 && (
<div style={{ textAlign: 'center' }}>
<div style={{ fontSize: 56, marginBottom: 16 }}>🎉</div>
<h2 style={{ fontSize: 24, fontWeight: 700, color: 'var(--navy)', marginBottom: 10 }}>
You're all set!
</h2>
<p style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 24, lineHeight: 1.6 }}>
{intent === 'hire' && "Let's find something cool to hire nearby."}
{intent === 'earn' && "Let's get your first item listed — only takes a few minutes."}
{intent === 'both' && "Browse listings or list your first item to get started."}
{!intent && "Browse listings or list your own item to get started."}
</p>

<div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
{(intent === 'hire' || intent === 'both' || !intent) && (
<button
className={intent === 'earn' ? 'btn btn-white btn-lg btn-full' : 'btn btn-gradient btn-lg btn-full'}
onClick={() => navigate('/')}
>
🔍 Browse listings
</button>
)}
{(intent === 'earn' || intent === 'both' || !intent) && (
<button
className={intent === 'hire' ? 'btn btn-white btn-lg btn-full' : 'btn btn-gradient btn-lg btn-full'}
onClick={() => navigate('/list-item')}
>
💰 List your first item
</button>
)}
</div>

<button
onClick={() => navigate('/how-it-works')}
style={{ background: 'none', border: 'none', color: 'var(--primary)', fontSize: 13, cursor: 'pointer', fontWeight: 500 }}
>
Learn how HireIt works →
</button>
</div>
)}
</div>
</div>
)
}